/**
 * Reference panel for the broker registration wizard.
 * Shows broker- and step-specific help content and security note.
 */

import { useState } from 'react';
import { Box, Typography, Link, Paper, Stack, Dialog, DialogContent, DialogTitle, IconButton, Tooltip } from '@mui/material';
import {
  Security as SecurityIcon,
  Lightbulb as HelpIcon,
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { getReferenceContent } from './brokerRegistrationConfig';

interface ReferencePanelProps {
  brokerType: string;
  stepIndex: number;
  /** When true, render in compact/collapsible-friendly style (e.g. for mobile). */
  compact?: boolean;
}

export function ReferencePanel({ brokerType, stepIndex, compact = false }: ReferencePanelProps) {
  const { title, body, links, securityNote, imagePath, instructions } = getReferenceContent(brokerType, stepIndex);
  const [imageOpen, setImageOpen] = useState<{ open: boolean; path: string; title: string }>({ open: false, path: '', title: '' });
  const [copiedText, setCopiedText] = useState<string | null>(null);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(text);
      setTimeout(() => setCopiedText(null), 2000);
    } catch (err) {
      console.error('Failed to copy text', err);
    }
  };

  const handleImageClick = (path: string, imgTitle: string) => {
    setImageOpen({ open: true, path, title: imgTitle });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: compact ? 2 : 3,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.05)',
        bgcolor: '#ffffff',
        height: compact ? 'auto' : 'fit-content',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.02)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(4, 55, 123, 0.05)',
            color: '#04377b',
            p: 1,
            borderRadius: 1.5,
          }}
        >
          <HelpIcon fontSize="small" />
        </Box>
        <Typography variant="subtitle2" fontWeight={700} color="#04377b" sx={{ letterSpacing: '0.01em' }}>
          {title}
        </Typography>
      </Box>
      {body && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7, opacity: 0.9 }}>
          {body}
        </Typography>
      )}

      {instructions && instructions.length > 0 && (
        <Stack spacing={3} sx={{ mb: 3 }}>
          {instructions.map((inst, idx) => (
            <Box key={idx}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1 }}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    bgcolor: 'rgba(4, 55, 123, 0.1)',
                    color: '#04377b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                >
                  {idx + 1}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {inst.text}
                </Typography>
              </Box>

              {inst.links && inst.links.length > 0 && (
                <Stack spacing={1} sx={{ ml: 4, mb: 1, mt: 0.5 }}>
                  {inst.links.map((link, i) => (
                    <Link
                      key={i}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: '#04377b',
                        textDecoration: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          textDecoration: 'underline',
                        }
                      }}
                    >
                      {link.text}
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </Link>
                  ))}
                </Stack>
              )}

              {inst.copyText && (
                <Box
                  sx={{
                    ml: 4,
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: 'rgba(0, 0, 0, 0.03)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    borderRadius: 1.5,
                    p: 1,
                    pl: 1.5,
                  }}
                >
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#333', wordBreak: 'break-all' }}>
                    {inst.copyText}
                  </Typography>
                  <Tooltip title={copiedText === inst.copyText ? "Copied!" : "Copy"}>
                    <IconButton size="small" onClick={() => handleCopy(inst.copyText!)}>
                      {copiedText === inst.copyText ? (
                        <CheckIcon fontSize="small" color="success" />
                      ) : (
                        <ContentCopyIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              {inst.imagePath && (
                <Box sx={{ ml: 4, mt: 1, textAlign: 'left' }}>
                  <Box
                    component="img"
                    src={inst.imagePath}
                    alt={inst.text}
                    onClick={() => handleImageClick(inst.imagePath!, title)}
                    sx={{
                      width: '100%',
                      maxWidth: 320,
                      height: compact ? 100 : 140,
                      objectFit: 'cover',
                      objectPosition: 'top',
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      border: '3px solid',
                      borderColor: 'rgba(4, 55, 123, 0.1)',
                      boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
                      transition: 'transform 0.2s',
                      '&:hover': { transform: 'scale(1.02)' }
                    }}
                  />
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      )}
      {links && links.length > 0 && (
        <Stack spacing={1} sx={{ mb: 3 }}>
          {links.map((link, i) => (
            <Link
              key={i}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: '#04377b',
                textDecoration: 'none',
                fontWeight: 600,
                '&:hover': {
                  textDecoration: 'underline',
                }
              }}
            >
              {link.text}
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Link>
          ))}
        </Stack>
      )}
      {imagePath && (
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Box
            component="img"
            src={imagePath}
            alt={`${title} screenshot`}
            onClick={() => handleImageClick(imagePath, title)}
            sx={{
              width: '100%',
              maxWidth: 320,
              height: compact ? 100 : 140,
              objectFit: 'cover',
              objectPosition: 'top',
              borderRadius: 1.5,
              cursor: 'pointer',
              border: '3px solid',
              borderColor: 'rgba(4, 55, 123, 0.1)',
              boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.02)' }
            }}
          />
        </Box>
      )}
      <Dialog open={imageOpen.open} onClose={() => setImageOpen({ ...imageOpen, open: false })} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={700} color="#04377b">{imageOpen.title}</Typography>
          <IconButton onClick={() => setImageOpen({ ...imageOpen, open: false })} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#f8f9fa' }}>
          <img src={imageOpen.path} alt={`Screenshot`} style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }} />
        </DialogContent>
      </Dialog>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          pt: 2.5,
          borderTop: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.05)',
        }}
      >
        <SecurityIcon sx={{ color: 'success.main', fontSize: 18, mt: 0.1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6, display: 'block' }}>
          <strong>Secure Connection:</strong> {securityNote}
        </Typography>
      </Box>
    </Paper>
  );
}
